import { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { SocialIcon } from "react-social-icons";

import { Composer, Container, MessageList, useWebchat } from "@botpress/webchat";
import { PermIdentity } from "@mui/icons-material";
import { FormControlLabel, MenuItem, Slider, Stack } from "@mui/material";
import { alpha, Box, Typography } from "@mui/material";
import { Button } from "@mui/material";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";

import constants from "../../assets/constants.js";
import demoConstants from "../../assets/demoConstants.js";
import { convertTextToSpeech } from "../../utils/googleTts.js";
import AppBarIconButton from "../ui/appBarIconButton";
import OmniAvatar from "../ui/omniAvatar.js";
import OmniCheckbox from "../ui/omniCheckbox.js";
import OmniTextField from "../ui/omniTextField.js";
import OmniTextToSpeechPlayer from "../ui/OmniTextToSpeechPlayer.js";
import Portal from "../ui/portal";
import PortalAppBar from "../ui/portalAppBar.js";
import PortalGridContent from "../ui/portalGridContent.js";

const formatAnnualIncome = value => {
  const amount = Number(value ?? 30000);
  return `${Math.round(amount / 1000)}k`;
};

const formatMarkerLabel = marker => {
  return marker
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, match => match.toUpperCase());
};

const getProfileMarkers = (profileConfig, draftOverrides = {}) => {
  const financialProfile = profileConfig?.financialProfile ?? {};
  const draft = draftOverrides ?? {};
  const markers = [];

  const isHomeOwner = draft.isHomeOwner ?? financialProfile?.housing?.isHomeOwner ?? false;
  const hasMortgage =
    draft.hasMortgage ?? financialProfile?.housing?.mortgage?.hasMortgage ?? false;
  const hasAutoLoan = draft.hasAutoLoan ?? financialProfile?.vehicles?.hasVehicleLoan ?? false;
  const hasCreditCardDebt =
    draft.hasCreditCardDebt ?? financialProfile?.credit?.creditCardDebt?.hasDebt ?? false;
  const hasChecking = draft.hasChecking ?? financialProfile?.banking?.hasChecking ?? false;
  const hasSavings = draft.hasSavings ?? financialProfile?.banking?.hasSavings ?? false;
  const hasDirectDeposit =
    draft.hasDirectDeposit ?? financialProfile?.banking?.hasDirectDeposit ?? false;
  const isBusinessOwner =
    draft.isBusinessOwner ?? financialProfile?.business?.isBusinessOwner ?? false;
  const planningRetirement =
    draft.planningRetirement ?? financialProfile?.lifeEvents?.planningRetirement ?? false;

  if (isHomeOwner) {
    markers.push("HOMEOWNER");
  }

  if (hasMortgage) {
    markers.push("HAS_MORTGAGE");
  }

  if (hasAutoLoan) {
    markers.push("HAS_AUTO_LOAN");
  }

  if (hasCreditCardDebt) {
    markers.push("HIGH_CREDIT_CARD_DEBT");
    markers.push("HAS_CREDIT_CARD_DEBT");
  }

  if (hasChecking) {
    markers.push("HAS_CHECKING");
  }

  if (hasSavings) {
    markers.push("HAS_SAVINGS");
  }

  if (hasDirectDeposit) {
    markers.push("HAS_DIRECT_DEPOSIT");
  }

  if (isBusinessOwner) {
    markers.push("BUSINESS_OWNER");
  }

  if (planningRetirement) {
    markers.push("APPROACHING_RETIREMENT");
  }

  return markers;
};

export const createDemoProfileConfiguration = (profile, draftOverrides) => {
  const safeDraftOverrides = draftOverrides ?? {};
  const profileName =
    safeDraftOverrides.displayName?.trim() ||
    [profile?.firstName, profile?.lastName].filter(Boolean).join(" ").trim() ||
    profile?.displayName ||
    "";

  const defaultFinancialProfile = demoConstants?.defaultFinancialProfile ?? {};
  const financialProfile = profile?.financialProfile
    ? JSON.parse(JSON.stringify(profile.financialProfile))
    : JSON.parse(JSON.stringify(defaultFinancialProfile));

  if (financialProfile?.demographics) {
    financialProfile.demographics = {
      ...financialProfile.demographics,
      annualIncome:
        safeDraftOverrides.annualIncome ?? financialProfile.demographics.annualIncome ?? null,
    };
  }

  if (financialProfile?.housing) {
    const isHomeOwner =
      safeDraftOverrides.isHomeOwner ?? financialProfile.housing.isHomeOwner ?? false;
    financialProfile.housing = {
      ...financialProfile.housing,
      isHomeOwner,
      isRenter: isHomeOwner ? false : (financialProfile.housing.isRenter ?? false),
      mortgage: {
        ...financialProfile.housing.mortgage,
        hasMortgage:
          safeDraftOverrides.hasMortgage ?? financialProfile.housing.mortgage?.hasMortgage ?? false,
      },
    };
  }

  if (financialProfile?.vehicles) {
    financialProfile.vehicles = {
      ...financialProfile.vehicles,
      hasVehicleLoan:
        safeDraftOverrides.hasAutoLoan ?? financialProfile.vehicles.hasVehicleLoan ?? false,
    };
  }

  if (financialProfile?.credit) {
    financialProfile.credit = {
      ...financialProfile.credit,
      creditCardDebt: {
        ...financialProfile.credit.creditCardDebt,
        hasDebt:
          safeDraftOverrides.hasCreditCardDebt ??
          financialProfile.credit.creditCardDebt?.hasDebt ??
          false,
      },
    };
  }

  if (financialProfile?.banking) {
    financialProfile.banking = {
      ...financialProfile.banking,
      hasChecking: safeDraftOverrides.hasChecking ?? financialProfile.banking.hasChecking ?? false,
      hasSavings: safeDraftOverrides.hasSavings ?? financialProfile.banking.hasSavings ?? false,
      hasDirectDeposit:
        safeDraftOverrides.hasDirectDeposit ?? financialProfile.banking.hasDirectDeposit ?? false,
    };
  }

  if (financialProfile?.business) {
    financialProfile.business = {
      ...financialProfile.business,
      isBusinessOwner:
        safeDraftOverrides.isBusinessOwner ?? financialProfile.business.isBusinessOwner ?? false,
    };
  }

  if (financialProfile?.lifeEvents) {
    financialProfile.lifeEvents = {
      ...financialProfile.lifeEvents,
      planningRetirement:
        safeDraftOverrides.planningRetirement ??
        financialProfile.lifeEvents.planningRetirement ??
        false,
    };
  }

  return {
    memberId: profile?.memberId ?? "",
    firstName: profile?.firstName ?? "",
    lastName: profile?.lastName ?? "",
    displayName: profileName,
    story: safeDraftOverrides.story?.trim() || profile?.story || "",
    lastUpdated: profile?.lastUpdated ?? "",
    financialProfile,
    runtimeOverrides: {
      isHomeOwner: safeDraftOverrides.isHomeOwner ?? null,
    },
  };
};

const DemoDirectionsPortal = props => {
  const theme = useTheme();

  let webchatClientID = "c280c28f-965f-407c-8785-fd9dee2b6dc0";

  const demoConfig = demoConstants ?? {};
  const demoUserProfiles = Array.isArray(demoConfig.demoUserProfiles)
    ? demoConfig.demoUserProfiles
    : [];
  const availableProducts = Array.isArray(demoConfig.products) ? demoConfig.products : [];

  const demoProfileTemplates = [
    {
      memberId: "0",
      firstName: "Anonymous",
      lastName: "User",
      displayName: "Anonymous User",
      story:
        "This visitor found the credit union through an online search while looking for ways to reduce monthly expenses.",
      lastUpdated: "",
      memberMarkers: JSON.parse(JSON.stringify(demoConfig.defaultFinancialProfile ?? {})),
    },
    ...demoUserProfiles,
  ];

  const [selectedProfileIndex, setSelectedProfileIndex] = useState(0);
  const [profileDrafts, setProfileDrafts] = useState(() =>
    demoProfileTemplates.map(profile => ({
      displayName:
        profile.displayName || `${profile.firstName ?? ""} ${profile.lastName ?? ""}`.trim(),
      story: profile.story ?? "",
      annualIncome: profile.financialProfile?.demographics?.annualIncome ?? 30000,
      isHomeOwner: profile.financialProfile?.housing?.isHomeOwner ?? false,
      hasMortgage: profile.financialProfile?.housing?.mortgage?.hasMortgage ?? false,
      hasAutoLoan: profile.financialProfile?.vehicles?.hasVehicleLoan ?? false,
      hasCreditCardDebt: profile.financialProfile?.credit?.creditCardDebt?.hasDebt ?? false,
      hasChecking: profile.financialProfile?.banking?.hasChecking ?? false,
      hasSavings: profile.financialProfile?.banking?.hasSavings ?? false,
      hasDirectDeposit: profile.financialProfile?.banking?.hasDirectDeposit ?? false,
      isBusinessOwner: profile.financialProfile?.business?.isBusinessOwner ?? false,
      planningRetirement: profile.financialProfile?.lifeEvents?.planningRetirement ?? false,
    })),
  );

  const activeProfileDraft = profileDrafts[selectedProfileIndex] ?? null;
  const activeDemoProfile = createDemoProfileConfiguration(
    demoProfileTemplates[selectedProfileIndex],
    activeProfileDraft,
  );
  const activeProfileMarkers = getProfileMarkers(activeDemoProfile, activeProfileDraft);

  const selectedProfile = demoProfileTemplates[selectedProfileIndex] ?? null;
  const isAnonymousProfile =
    selectedProfile?.memberId === "0" ||
    selectedProfile?.firstName === "Anonymous" ||
    selectedProfile?.displayName === "Anonymous User";

  // 1. Turned matchingProducts into state
  const [matchingProducts, setMatchingProducts] = useState([]);

  // 2. Setup an effect to keep matchingProducts sync'd up with profiles/markers
  useEffect(() => {
    const filtered = availableProducts.filter(product =>
      product?.markers?.some(marker => activeProfileMarkers.includes(marker?.marker)),
    );
    setMatchingProducts(filtered);
  }, [availableProducts, activeProfileMarkers]);

  //Flag Defs:
  //  languageCode - the code
  //  tts- bool

  //#region languageCode and TTS Parameter Handling
  const checkForLanguageCodeParam = () => {
    const urlParams = new URLSearchParams(window.location.search);
    for (var [key, value] of urlParams.entries()) {
      if (key === "languageCode") {
        return value;
      }
    }
    return "en-US";
  };

  const [languageCode] = useState(checkForLanguageCodeParam());

  const [ttsValue, setTTSValue] = useState(false);

  useEffect(() => {
    if (ttsValue != null) {
      setIsWebchatOpen(true);
    }
  }, [ttsValue]);

  //#endregion

  const config = {
    botDescription: "Directions Credit Union Member Concierge",
    botName: "Miles",
    botAvatar:
      "https://omni-public-media.s3.us-west-2.amazonaws.com/branding/directions/directions.png",
    title: "Directions Credit Union",
    customColors: {
      commonGround: theme.palette.commonground.main,
      primaryContrastText: theme.palette.primary.contrastText,
      secondaryContrastText: theme.palette.secondary.contrastText,
      primaryColor: theme.palette.primary.main,
      secondaryColor: theme.palette.secondary.main,
    },
  };

  const [isWebchatOpen, setIsWebchatOpen] = useState(false);

  const { client, messages, isTyping, user, clientState, newConversation, on } = useWebchat({
    clientId: webchatClientID, // Insert your Client ID here

    storageKey: `botpress-DemoDirectionsPortal-${webchatClientID}`,
    storageLocation: "localStorage",
  });

  const enrichedMessages = messages.map(message => {
    const { authorId } = message;
    const direction = authorId === user?.userId ? "outgoing" : "incoming";
    return {
      ...message,
      direction,
      sender:
        direction === "outgoing"
          ? { name: user?.name ?? "You", avatar: user?.pictureUrl }
          : { name: config.botName ?? "Bot", avatar: config.botAvatar },
    };
  });

  //#region User Data Update
  const [userInitialized, setUserInitialized] = useState(false);

  const syncProfileToWebchat = (profileIndex, { startConversation = false } = {}) => {
    const selectedProfile = demoProfileTemplates[profileIndex];
    const profilePayload = createDemoProfileConfiguration(
      selectedProfile,
      profileDrafts[profileIndex],
    );

    const updatePayload = {
      userId: selectedProfile?.memberId?.toString?.() ?? "0",
      displayName: selectedProfile.firstName,
      languageCode: languageCode,
      origin: constants.botpressConversationOriginEnums.Portal,
      financialProfile: JSON.stringify(profilePayload),
      products: JSON.stringify(matchingProducts),
    };

    if (!client) {
      return Promise.resolve();
    }

    if (startConversation) {
      return newConversation().then(() => {
        client.updateUser({ data: updatePayload });
        setIsWebchatOpen(true);
      });
    }

    client.updateUser({ data: updatePayload });
    return Promise.resolve();
  };

  useEffect(() => {
    if (client && !userInitialized) {
      try {
        syncProfileToWebchat(0);
        setUserInitialized(true);
      } catch (error) {
        console.error(error);
      }
    }
  }, [client, demoProfileTemplates, languageCode, profileDrafts, userInitialized]);
  //#endregion

  //#region Menu HandlersOmni Header
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  //#endregion

  //#region Text to Speech Player Handlers

  const [volume, setVolume] = useState(0.5);
  const [textToSpeechQueue, setTextToSpeechQueue] = useState([]);

  const [speechConfig] = useState({
    voiceName: "Despina",
    languageCode: languageCode,
    speakingRate: 1.2,
  });

  useEffect(() => {
    const unsubscribe = on("message", message => {
      if (message.authorId !== user?.userId) {
        try {
          let messageText = message.block.block.text;
          convertTextToSpeech(messageText, speechConfig).then(url => {
            setTextToSpeechQueue(prev => [...prev, url]);
          });
        } catch (error) {
          console.error("Error in TTS message handling:", error);
        }
      }
    });

    return () => {
      unsubscribe?.();
    };
  }, [on, speechConfig, user]);

  //#endregion
  const updateProfileDraft = (field, value) => {
    setProfileDrafts(prev =>
      prev.map((draft, index) =>
        index === selectedProfileIndex ? { ...draft, [field]: value } : draft,
      ),
    );
  };

  const startWebchat = profileIndex => {
    syncProfileToWebchat(profileIndex, { startConversation: true });
  };
  return (
    <Portal
      displayHeader={false}
      sx={{
        backgroundSize: "cover",
        border: "none",
        backgroundColor: theme.palette.commonground.main,
      }}
    >
      <PortalAppBar
        title="Sales Behind PIN Demo"
        belowTitle="Directions Credit Union"
        hideAvatar={true}
      />

      <PortalGridContent
        hasPortalAppBar={true}
        size={{ xs: 12, md: 6 }}
        width={"100%"}
        height={"100%"}
      >
        <Grid container item spacing={2} size={{ xs: 12 }}>
          <Grid id={"profiles"} item spacing={2} size={{ xs: 12, sm: 7 }}>
            <Grid
              item
              size={{ xs: 12 }}
              sx={{
                borderRadius: theme.spacing(1),
                backgroundColor: theme.palette.primary.contrastText,
                border: `1px solid ${alpha(theme.palette.common.black, 0.15)}`,
                boxShadow: theme.shadows[2],
                padding: theme.spacing(2),
              }}
            >
              <Typography variant="h6" sx={{ marginBottom: theme.spacing(1) }}>
                Configure a demo profile
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginBottom: theme.spacing(2) }}
              >
                Choose a preconfigured profile and adjust the settings that should influence the
                conversation. Then click Start Conversation.
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", marginBottom: theme.spacing(2) }}
              >
                These settings are intended for logged-in users, where the system can determine the
                appropriate profile details. Anonymous users cannot have those values inferred, so
                the controls are disabled for that experience.
              </Typography>

              <Grid container spacing={2}>
                <Grid item size={{ xs: 12 }}>
                  <OmniTextField
                    select
                    label="Profile"
                    value={selectedProfileIndex}
                    onChange={event => {
                      const nextIndex = Number(event.target.value);
                      setSelectedProfileIndex(nextIndex);
                    }}
                  >
                    {demoProfileTemplates.map((profile, index) => (
                      <MenuItem key={profile.memberId ?? index} value={index}>
                        {profile.displayName ||
                          `${profile.firstName ?? ""} ${profile.lastName ?? ""}`.trim()}
                      </MenuItem>
                    ))}
                  </OmniTextField>
                </Grid>

                <Grid item size={{ xs: 12, md: 6 }}>
                  <OmniTextField
                    label="Display name"
                    value={activeProfileDraft?.displayName ?? ""}
                    disabled={isAnonymousProfile}
                    onChange={event => updateProfileDraft("displayName", event.target.value)}
                  />
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" sx={{ marginBottom: theme.spacing(1) }}>
                    Annual income: ${formatAnnualIncome(activeProfileDraft?.annualIncome ?? 30000)}
                  </Typography>
                  <Slider
                    value={activeProfileDraft?.annualIncome ?? 30000}
                    onChange={(_, value) => updateProfileDraft("annualIncome", value)}
                    disabled={isAnonymousProfile}
                    valueLabelDisplay="off"
                    sx={{ width: `calc(100% - 16px)`, marginLeft: "8px;" }}
                    min={30000}
                    max={150000}
                    step={1000}
                    marks={[
                      { value: 30000, label: "$30k" },
                      { value: 150000, label: "$150k" },
                    ]}
                  />
                </Grid>

                <Grid item size={{ xs: 12 }}>
                  <Typography variant="body2" sx={{ marginBottom: theme.spacing(1) }}>
                    Household and financial settings
                  </Typography>
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    disabled={isAnonymousProfile}
                    control={
                      <OmniCheckbox
                        checked={activeProfileDraft?.isHomeOwner ?? false}
                        disabled={isAnonymousProfile}
                        onChange={event => updateProfileDraft("isHomeOwner", event.target.checked)}
                      />
                    }
                    label="Homeowner"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    disabled={isAnonymousProfile}
                    control={
                      <OmniCheckbox
                        checked={activeProfileDraft?.hasMortgage ?? false}
                        disabled={isAnonymousProfile}
                        onChange={event => updateProfileDraft("hasMortgage", event.target.checked)}
                      />
                    }
                    label="Has mortgage"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    disabled={isAnonymousProfile}
                    control={
                      <OmniCheckbox
                        checked={activeProfileDraft?.hasAutoLoan ?? false}
                        disabled={isAnonymousProfile}
                        onChange={event => updateProfileDraft("hasAutoLoan", event.target.checked)}
                      />
                    }
                    label="Has auto loan"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    disabled={isAnonymousProfile}
                    control={
                      <OmniCheckbox
                        checked={activeProfileDraft?.hasCreditCardDebt ?? false}
                        disabled={isAnonymousProfile}
                        onChange={event =>
                          updateProfileDraft("hasCreditCardDebt", event.target.checked)
                        }
                      />
                    }
                    label="Has credit card debt"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    disabled={isAnonymousProfile}
                    control={
                      <OmniCheckbox
                        checked={activeProfileDraft?.hasChecking ?? false}
                        disabled={isAnonymousProfile}
                        onChange={event => updateProfileDraft("hasChecking", event.target.checked)}
                      />
                    }
                    label="Has checking account"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    disabled={isAnonymousProfile}
                    control={
                      <OmniCheckbox
                        checked={activeProfileDraft?.hasSavings ?? false}
                        disabled={isAnonymousProfile}
                        onChange={event => updateProfileDraft("hasSavings", event.target.checked)}
                      />
                    }
                    label="Has savings account"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    disabled={isAnonymousProfile}
                    control={
                      <OmniCheckbox
                        checked={activeProfileDraft?.hasDirectDeposit ?? false}
                        disabled={isAnonymousProfile}
                        onChange={event =>
                          updateProfileDraft("hasDirectDeposit", event.target.checked)
                        }
                      />
                    }
                    label="Has direct deposit"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    disabled={isAnonymousProfile}
                    control={
                      <OmniCheckbox
                        checked={activeProfileDraft?.isBusinessOwner ?? false}
                        disabled={isAnonymousProfile}
                        onChange={event =>
                          updateProfileDraft("isBusinessOwner", event.target.checked)
                        }
                      />
                    }
                    label="Business owner"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    disabled={isAnonymousProfile}
                    control={
                      <OmniCheckbox
                        checked={activeProfileDraft?.planningRetirement ?? false}
                        disabled={isAnonymousProfile}
                        onChange={event =>
                          updateProfileDraft("planningRetirement", event.target.checked)
                        }
                      />
                    }
                    label="Planning retirement"
                  />
                </Grid>

                <Grid item size={{ xs: 12 }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => startWebchat(selectedProfileIndex)}
                    sx={{ width: "100%" }}
                  >
                    Start conversation
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Grid
              item
              size={{ xs: 12 }}
              sx={{
                marginTop: theme.spacing(2),
                borderRadius: theme.spacing(1),
                backgroundColor: theme.palette.primary.contrastText,
                border: `1px solid ${alpha(theme.palette.common.black, 0.15)}`,
                boxShadow: theme.shadows[2],
                padding: theme.spacing(2),
              }}
            >
              <Typography variant="h6" sx={{ marginBottom: theme.spacing(1) }}>
                Suggested products
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginBottom: theme.spacing(2) }}
              >
                Products that match the current profile marker set.
              </Typography>
              <Stack spacing={1}>
                {matchingProducts.length > 0 ? (
                  matchingProducts.map(product => {
                    const productMarkers = product?.markers ?? [];
                    const matchedMarkers = new Set(
                      productMarkers
                        .map(markerInfo => markerInfo?.marker)
                        .filter(marker => activeProfileMarkers.includes(marker)),
                    );

                    return (
                      <Box
                        key={product.id}
                        sx={{
                          border: `1px solid ${alpha(theme.palette.common.black, 0.12)}`,
                          borderRadius: theme.spacing(1),
                          padding: theme.spacing(1.5),
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 2,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {product.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {product.summary}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: 0.75,
                            minWidth: 180,
                          }}
                        >
                          {productMarkers.map(markerInfo => {
                            const isMatch = matchedMarkers.has(markerInfo?.marker);
                            return (
                              <Box
                                key={`${product.id}-${markerInfo.marker}`}
                                sx={{
                                  borderRadius: 999,
                                  px: 1,
                                  py: 0.35,
                                  backgroundColor: isMatch
                                    ? alpha(theme.palette.success.main, 0.16)
                                    : alpha(theme.palette.grey[500], 0.16),
                                  color: isMatch
                                    ? theme.palette.success.main
                                    : theme.palette.text.secondary,
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {formatMarkerLabel(markerInfo.marker)}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No products matched the selected settings yet.
                  </Typography>
                )}
              </Stack>
            </Grid>
          </Grid>

          <Grid id={"chat"} item spacing={2} size={{ xs: 12, sm: 5 }} width={"100%"}>
            {isWebchatOpen === true && (
              <Container
                connected={clientState !== "disconnected"}
                style={{
                  height: "calc(100vh - 82px)",
                  width: "100%",
                  display: isWebchatOpen ? "flex" : "none",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    border: "none",
                    boxShadow: theme.shadows[2],
                    zIndex: 1,
                    columnGap: "8px",
                    paddingTop: "12px",
                    paddingBottom: "12px",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    backgroundColor: `${config.customColors.primaryColor}dd`,
                  }}
                >
                  <OmniAvatar
                    sx={{
                      width: "64px",
                      height: "64px",
                      boxShadow: theme.shadows[2],
                      background: "radial-gradient(#fdfef400, #aaaaaa)",
                    }}
                    src={config.botAvatar}
                  />
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      color: config.customColors.primaryContrastText,
                    }}
                  >
                    <Typography
                      variant="h5"
                      fontWeight={"500"}
                      sx={{
                        textShadow: `1px 1px 1px ${config.customColors.secondaryContrastText}88`,
                      }}
                    >
                      {config.title}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        marginTop: "-6px",
                      }}
                    >
                      {config.botName}
                    </Typography>
                  </Box>
                  {config && config.socialMedia ? (
                    <Box display={"flex"} columnGap={"0px"}>
                      {config.socialMedia.map(social => (
                        <AppBarIconButton>
                          <SocialIcon
                            target="_blank"
                            url={social}
                            bgColor="transparent"
                            style={{
                              height: 36,
                              width: 36,
                            }}
                            fgColor={config.customColors.primaryContrastText}
                          />
                        </AppBarIconButton>
                      ))}
                    </Box>
                  ) : null}
                </Box>

                <MessageList
                  botAvatar={config.botAvatar}
                  botName={config.botName}
                  botDescription={config.botDescription}
                  isTyping={isTyping}
                  headerMessage="Chat History"
                  showMarquee={true}
                  messages={enrichedMessages}
                  sendMessage={client?.sendMessage}
                  showIncomingMessageAvatar={true}
                />

                <Composer
                  disableComposer={isTyping}
                  isReadOnly={false}
                  allowFileUpload={true}
                  connected={clientState !== "disconnected"}
                  sendMessage={client?.sendMessage}
                  uploadFile={client?.uploadFile}
                  composerPlaceholder="Type a message..."
                />
                {ttsValue === true && (
                  <Box
                    style={{
                      position: "absolute",
                      right: 58,
                      bottom: 21,
                      zIndex: 1001,
                      height: 32,
                      width: 32,
                      color: "#5f6276",
                    }}
                  >
                    <OmniTextToSpeechPlayer
                      textToSpeechQueue={textToSpeechQueue}
                      setTextToSpeechQueue={setTextToSpeechQueue}
                      volume={volume}
                      setVolume={setVolume}
                    />
                  </Box>
                )}
              </Container>
            )}
          </Grid>
        </Grid>
      </PortalGridContent>
    </Portal>
  );
};

export default withRouter(DemoDirectionsPortal);
